import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Message, Form, Input, List, Header, Icon, Loader, Segment } from 'semantic-ui-react'
import './deleteModal.css';

/**
 * Enhanced Delete Book Modal
 * Provides comprehensive delete confirmation with impact analysis
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Object} props.book - Book to delete
 * @param {Function} props.onConfirm - Callback when delete is confirmed
 * @param {Function} props.onCancel - Callback when delete is cancelled
 * @param {Function} props.getDeleteImpact - Function to fetch delete impact analysis
 * @param {boolean} props.loading - Whether delete operation is in progress
 * @returns {JSX.Element} DeleteBookModal component
 */
const DeleteBookModal = ({
  open,
  book,
  onConfirm,
  onCancel,
  getDeleteImpact,
  loading = false
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [impactData, setImpactData] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactError, setImpactError] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setConfirmationText('');
      setImpactData(null);
      setImpactError(null);
    }
  }, [open]);

  // Fetch impact analysis when modal opens and book is available
  useEffect(() => {
    if (open && book && book.uuid && getDeleteImpact) {
      setImpactLoading(true);
      setImpactError(null);

      getDeleteImpact(book.uuid)
        .then(data => {
          console.log('Delete impact data:', data);
          setImpactData(data.impactAnalysis || data);
          setImpactLoading(false);
        })
        .catch(error => {
          console.error('Error fetching delete impact:', error);
          setImpactError('Failed to load impact analysis. You can still proceed with deletion.');
          setImpactLoading(false);
        });
    }
  }, [open, book, getDeleteImpact]);

  // Handle confirmation text change
  const handleConfirmationTextChange = useCallback((e, { value }) => {
    setConfirmationText(value);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(() => {
    if (onConfirm) {
      onConfirm('hard'); // Always hard delete for books
    }
  }, [onConfirm]);

  // Check if user can confirm deletion
  const canConfirm = confirmationText.toUpperCase() === 'DELETE' && !loading && !impactLoading;

  // Get book display name
  const getBookDisplayName = () => {
    return book?.name || 'Untitled Book';
  };

  return (
    <Modal open={open} size="small" closeOnDimmerClick={false}
      style={{ zIndex: 10000 }}
      dimmer={{ style: { zIndex: 9999 } }}
    >
      <Modal.Header>
        <Icon name="warning sign" color="red" />
        Permanently Delete Book
      </Modal.Header>

      <Modal.Content>
        {/* Book Info */}
        {book && (
          <Message info>
            <Message.Header>You are about to permanently delete:</Message.Header>
            <p><strong>{getBookDisplayName()}</strong></p>
            {book && book.author && (
              <p>by <em>{book.author}</em></p>
            )}
          </Message>
        )}

        {/* Impact Analysis */}
        <Header as="h4" dividing>
          <Icon name="list" />
          Impact Analysis
        </Header>

        {impactLoading ? (
          <Segment>
            <Loader active inline="centered" />
            <p style={{ textAlign: 'center', marginTop: '10px' }}>
              Analyzing what will be affected...
            </p>
          </Segment>
        ) : impactError ? (
          <Message warning>
            <Message.Header>Impact Analysis Unavailable</Message.Header>
            <p>{impactError}</p>
          </Message>
        ) : impactData ? (
          <Segment>
            <p><strong>This will permanently delete:</strong></p>
            <List bulleted>
              <List.Item>
                <strong>Book:</strong> "{getBookDisplayName()}" with all content
              </List.Item>
              <List.Item>
                <strong>All Characters:</strong> Every character in this book with their profiles
              </List.Item>
              <List.Item>
                <strong>All Variations:</strong> Every voice and text variation for all characters
              </List.Item>
              <List.Item>
                <strong>All Speech Samples:</strong> Audio files, text files, and recordings
              </List.Item>
              <List.Item>
                <strong>Book Cover:</strong> Cover image and associated files
              </List.Item>
              <List.Item>
                <strong>Book Profile:</strong> Description, metadata, and settings
              </List.Item>
              {book && book.author && (
                <List.Item>
                  <strong>Author:</strong> Will be deleted if not used by other books
                </List.Item>
              )}
            </List>
            {impactData.message && (
              <Message>
                <p>{impactData.message}</p>
              </Message>
            )}
          </Segment>
        ) : (
          <Message>
            <p>Impact analysis will be performed when you open this dialog.</p>
          </Message>
        )}

        {/* Warning Message */}
        <Message negative>
          <Message.Header>
            <Icon name="exclamation triangle" />
            Warning: Permanent Deletion
          </Message.Header>
          <p>
            This action will <strong>permanently delete</strong> all book data including:
          </p>
          <List bulleted>
            <List.Item>All database records</List.Item>
            <List.Item>All S3 files (audio, images, documents)</List.Item>
            <List.Item>All character profiles and variations</List.Item>
            <List.Item>All speech samples and recordings</List.Item>
          </List>
          <p>
            <strong>This action cannot be undone.</strong> Make sure you have backups if needed.
          </p>
        </Message>

        {/* Confirmation Input */}
        <Header as="h4" dividing>
          <Icon name="keyboard" />
          Confirmation Required
        </Header>

        <Form.Field required>
          <label>Type "DELETE" to confirm this permanent action:</label>
          <Input
            placeholder="DELETE"
            value={confirmationText}
            onChange={handleConfirmationTextChange}
            disabled={loading || impactLoading}
            autoComplete="off"
            style={{ marginTop: '10px' }}
          />
        </Form.Field>

        {confirmationText.toUpperCase() === 'DELETE' && (
          <Message warning>
            <Message.Header>Ready to Delete</Message.Header>
            <p>You have confirmed the deletion. Click "Permanently Delete Book" to proceed.</p>
          </Message>
        )}
      </Modal.Content>

      <Modal.Actions>
        <Button
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          color="red"
          disabled={!canConfirm}
          loading={loading}
          onClick={handleConfirmDelete}
        >
          <Icon name="trash" />
          Permanently Delete Book
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default DeleteBookModal;
